import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { LABELS, PRIORIDADES } from '@/lib/constants'
import { frenteHex } from '@/lib/ambientes'
import type { TaskFrente, TaskPrioridade, TaskStatus, UserRole } from '@/types/database'

/** Bolinha de ambiente na cor real da marca (anel garante contraste no claro). */
export function FrenteDot({ frente, className }: { frente: TaskFrente; className?: string }) {
  return (
    <span
      className={cn(
        'inline-block h-2 w-2 shrink-0 rounded-full ring-1 ring-black/10 dark:ring-white/20',
        className,
      )}
      style={{ backgroundColor: frenteHex(frente) }}
    />
  )
}

export function FrenteBadge({ frente, className }: { frente: TaskFrente; className?: string }) {
  const hex = frenteHex(frente)
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold',
        className,
      )}
      style={{ backgroundColor: `${hex}22` }}
    >
      <FrenteDot frente={frente} className="h-1.5 w-1.5" />
      {LABELS.frente[frente]}
    </span>
  )
}

const priorityVariant: Record<TaskPrioridade, 'destructive' | 'warning' | 'default' | 'secondary'> =
  {
    critica: 'destructive',
    alta: 'warning',
    planejada: 'default',
    backlog: 'secondary',
  }

export function PriorityBadge({
  prioridade,
  className,
}: {
  prioridade: TaskPrioridade
  className?: string
}) {
  return (
    <Badge variant={priorityVariant[prioridade]} className={className}>
      {PRIORIDADES.find((p) => p.value === prioridade)?.label}
    </Badge>
  )
}

const statusTone: Record<TaskStatus, string> = {
  entrada: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
  aguardando_briefing: 'bg-amber-500/15 text-amber-600 dark:text-amber-300',
  pronto_para_produzir: 'bg-sky-500/15 text-sky-600 dark:text-sky-300',
  em_producao: 'bg-blue-500/15 text-blue-600 dark:text-blue-300',
  aguardando_material: 'bg-orange-500/15 text-orange-600 dark:text-orange-300',
  aguardando_aprovacao: 'bg-violet-500/15 text-violet-600 dark:text-violet-300',
  em_ajustes: 'bg-rose-500/15 text-rose-600 dark:text-rose-300',
  agendado: 'bg-teal-500/15 text-teal-600 dark:text-teal-300',
  publicado: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300',
  backlog_editorial: 'bg-zinc-500/15 text-zinc-600 dark:text-zinc-300',
}

export function StatusBadge({ status, className }: { status: TaskStatus; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        statusTone[status],
        className,
      )}
    >
      {LABELS.status[status]}
    </span>
  )
}

export function RoleBadge({ role }: { role: UserRole }) {
  return <Badge variant="outline">{LABELS.role[role]}</Badge>
}
